import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Schedule from './components/Schedule';
import VodGrid from './components/VodGrid';
import Games from './components/Games';
import YouTubeHighlights from './components/YouTubeHighlights';
import Contact from './components/Contact';
import Footer from './components/Footer';
import VideoModal from './components/VideoModal';
import { Toaster } from './components/ui/toaster';
import { AuthProvider, useAuth } from './context/AuthContext';
import { toast } from './hooks/use-toast';
import TopOfMonthFloat from './components/TopOfMonthFloat';
import LiveNowFloat from './components/LiveNowFloat';
import BackToHome from './components/BackToHome';
import PageviewTracker from './components/PageviewTracker';
import FollowerTicker from './components/FollowerTicker';
import StatsTicker from './components/StatsTicker';
import LoginDialog from './components/LoginDialog';
import { brand } from './mock';
import { useHashScroll } from './hooks/useHashScroll';
import { useInitialSplash } from './hooks/useInitialSplash';
import SplashScreen from './components/SplashScreen';

// ---------------------------------------------------------------------------
// Route-level code splitting: every page that isn't the homepage is loaded on
// demand. This keeps the initial JS bundle small — the admin panel, OBS
// overlays, and mini-games are only fetched when the user (or OBS Browser
// Source) actually visits their route.
// ---------------------------------------------------------------------------
const VotePage = lazy(() => import('./components/VotePage'));
const WatchPage = lazy(() => import('./components/WatchPage'));
const MusicPage = lazy(() => import('./components/MusicPage'));
const SetupPage = lazy(() => import('./components/SetupPage'));
const AdminPage = lazy(() => import('./components/AdminPage'));
const MascotThanksAdminPage = lazy(() => import('./components/admin/MascotThanksAdminPage'));
const ProfilePage = lazy(() => import('./components/ProfilePage'));
const SuggestionsHubPage = lazy(() => import('./components/SuggestionsHubPage'));
const HowToSuggestPage = lazy(() => import('./components/HowToSuggestPage'));
const SocialsPage = lazy(() => import('./components/SocialsPage'));
const TipPage = lazy(() => import('./components/TipPage'));
const DonateThanksPage = lazy(() => import('./components/DonateThanksPage'));
const BitsPage = lazy(() => import('./components/BitsPage'));
const SupportHubPage = lazy(() => import('./components/SupportHubPage'));
const ForumPage = lazy(() => import('./components/forum/ForumPage'));
const TopicPage = lazy(() => import('./components/forum/TopicPage'));
const GrijpmachinePage = lazy(() => import('./components/grijpmachine/GrijpmachinePage'));
const SlotmachinePage = lazy(() => import('./components/slotmachine/SlotmachinePage'));
const RadPage = lazy(() => import('./components/rad/RadPage'));
const GiveawayPage = lazy(() => import('./components/giveaway/GiveawayPage'));
const PollPage = lazy(() => import('./components/poll/PollPage'));
const PollsHubPage = lazy(() => import('./components/poll/PollsHubPage'));
const DrawPage = lazy(() => import('./components/draw/DrawPage'));
const ChannelPointsPage = lazy(() => import('./components/channel_points/ChannelPointsPage'));
const OnlyFansPage = lazy(() => import('./components/onlyfans/OnlyFansPage'));

// OBS overlays — heavy set (30+), only ever loaded by OBS Browser Sources.
const ObsPromo = lazy(() => import('./components/ObsPromo'));
const ObsSchedule = lazy(() => import('./components/ObsSchedule'));
const ObsGrijpmachine = lazy(() => import('./components/grijpmachine/ObsGrijpmachine'));
const ObsSlotmachine = lazy(() => import('./components/slotmachine/ObsSlotmachine'));
const ObsRad = lazy(() => import('./components/rad/ObsRad'));
const ObsGiveaway = lazy(() => import('./components/obs/ObsGiveaway'));
const ObsPoll = lazy(() => import('./components/obs/ObsPoll'));
const ObsPollFull = lazy(() => import('./components/obs/ObsPollFull'));
const ObsDraw = lazy(() => import('./components/obs/ObsDraw'));
const ObsStartingSoon = lazy(() => import('./components/obs/ObsStartingSoon'));
const ObsBrb = lazy(() => import('./components/obs/ObsBrb'));
const ObsEnding = lazy(() => import('./components/obs/ObsEnding'));
const ObsCamFrame = lazy(() => import('./components/obs/ObsCamFrame'));
const ObsCamFrameRound = lazy(() => import('./components/obs/ObsCamFrameRound'));
const ObsOzzyCam = lazy(() => import('./components/obs/ObsOzzyCam'));
const ObsStatsTicker = lazy(() => import('./components/obs/ObsStatsTicker'));
const ObsStatsPanel = lazy(() => import('./components/obs/ObsStatsPanel'));
const ObsFullcamStats = lazy(() => import('./components/obs/ObsFullcamStats'));
const ObsScreenCamStats = lazy(() => import('./components/obs/ObsScreenCamStats'));
const ObsHeartRate = lazy(() => import('./components/obs/ObsHeartRate'));
const ObsAlerts = lazy(() => import('./components/obs/ObsAlerts'));
const ObsMascotThanks = lazy(() => import('./components/obs/ObsMascotThanks'));
const ObsWebsitePromo = lazy(() => import('./components/obs/ObsWebsitePromo'));
const ObsChat = lazy(() => import('./components/obs/ObsChat'));
const ObsNowPlaying = lazy(() => import('./components/obs/ObsNowPlaying'));
const ObsGoalBar = lazy(() => import('./components/obs/ObsGoalBar'));
const ObsSocialBar = lazy(() => import('./components/obs/ObsSocialBar'));
const ObsBorderFrame = lazy(() => import('./components/obs/ObsBorderFrame'));
const ObsPodium = lazy(() => import('./components/obs/ObsPodium'));
const ObsStatsBorder = lazy(() => import('./components/obs/ObsStatsBorder'));
const ObsSubathonTimer = lazy(() => import('./components/obs/ObsSubathonTimer'));
const ObsMascotTalk = lazy(() => import('./components/obs/ObsMascotTalk'));
const ObsMascotCam = lazy(() => import('./components/obs/ObsMascotCam'));
const ObsMultiChat = lazy(() => import('./components/obs/ObsMultiChat'));
const ObsMultiChatDashboard = lazy(() => import('./components/obs/ObsMultiChatDashboard'));
const ObsRewardToast = lazy(() => import('./components/obs/ObsRewardToast'));
const ObsScoreboard = lazy(() => import('./components/obs/ObsScoreboard'));

// Fallback used while a lazy route is fetching. Kept transparent so it never
// flashes a "loading" bar on OBS Browser Sources — the overlay simply appears
// once its chunk is ready.
function RouteFallback() {
  return <div data-testid="route-fallback" style={{ minHeight: '100vh' }} />;
}

function InitialSplashGate() {
  useInitialSplash();
  return null;
}

function BrandBackground() {
  return (
    <div
      className="brand-bg"
      style={{ '--brand-wallpaper': `url(${brand.wallpaper})` }}
    />
  );
}

function notifyAuthResult(authParam) {
  if (authParam === 'success') {
    toast({ title: 'Ingelogd! \u{1F389}', description: 'Welkom op JorDievoorje.' });
    return;
  }
  if (authParam === 'error') {
    toast({ title: 'Login geannuleerd', description: 'Geen toegang verleend.' });
    return;
  }
  toast({ title: 'Login mislukt', description: 'Probeer het opnieuw.' });
}

function AuthCallbackHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();

  const handleAuthParam = useCallback(
    async (authParam) => {
      if (authParam === 'success') {
        await refresh();
      }
      notifyAuthResult(authParam);
      navigate('/', { replace: true });
    },
    [navigate, refresh]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const auth = params.get('auth');
    if (!auth) return;
    handleAuthParam(auth);
  }, [location.search, handleAuthParam]);

  return null;
}

function Home() {
  const [video, setVideo] = useState(null);
  useHashScroll();
  return (
    <div className="min-h-screen text-white selection:bg-violet-600/40 selection:text-white">
      <AuthCallbackHandler />
      <Navbar />
      <Hero />
      <Schedule />
      <VodGrid onPlay={setVideo} />
      <Games />
      <YouTubeHighlights onPlay={setVideo} />
      <Contact />
      <Footer />
      <VideoModal video={video} onClose={() => setVideo(null)} />
      <TopOfMonthFloat />
      <StatsTicker />
      <Toaster />
    </div>
  );
}

function VoteRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <VotePage />
      <BackToHome />
      <Footer />
      <Toaster />
    </>
  );
}

function WatchRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <WatchPage />
      <BackToHome />
      <Footer />
      <Toaster />
    </>
  );
}

function MusicRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <MusicPage />
      <BackToHome />
      <Footer />
      <Toaster />
    </>
  );
}

function SetupRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <SetupPage />
      <BackToHome />
      <Footer />
      <Toaster />
    </>
  );
}

function AdminRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <AdminPage />
      <Toaster />
    </>
  );
}

function MascotThanksAdminRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <MascotThanksAdminPage />
      <Toaster />
    </>
  );
}

function ProfileRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <ProfilePage />
      <BackToHome />
      <Footer />
      <Toaster />
    </>
  );
}

function SuggestionsHubRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <SuggestionsHubPage />
      <Footer />
      <Toaster />
    </>
  );
}

function HowToSuggestRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <HowToSuggestPage />
      <Footer />
      <Toaster />
    </>
  );
}

function SocialsRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <SocialsPage />
      <Footer />
      <Toaster />
    </>
  );
}

function TipRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <TipPage />
      <BackToHome />
      <Footer />
      <Toaster />
    </>
  );
}

function BitsRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <BitsPage />
      <BackToHome />
      <Footer />
      <Toaster />
    </>
  );
}

function SupportRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <SupportHubPage />
      <BackToHome />
      <Footer />
      <Toaster />
    </>
  );
}

function ForumRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <ForumPage />
      <BackToHome />
      <Footer />
      <Toaster />
    </>
  );
}

function TopicRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <TopicPage />
      <BackToHome />
      <Footer />
      <Toaster />
    </>
  );
}

function PollsHubRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <PollsHubPage />
      <BackToHome />
      <Footer />
      <Toaster />
    </>
  );
}

function ChannelPointsRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <ChannelPointsPage />
      <BackToHome />
      <Footer />
      <Toaster />
    </>
  );
}

function DrawRoute() {
  return (
    <>
      <AuthCallbackHandler />
      <Navbar />
      <DrawPage />
      <BackToHome />
      <Footer />
      <Toaster />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Shell />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function Shell() {
  const location = useLocation();
  const isObs = location.pathname.startsWith('/obs');

  if (isObs) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/obs/promo" element={<ObsPromo />} />
          <Route path="/obs/schedule" element={<ObsSchedule />} />
          <Route path="/obs/grijpmachine" element={<ObsGrijpmachine />} />
          <Route path="/obs/slotmachine" element={<ObsSlotmachine />} />
          <Route path="/obs/rad" element={<ObsRad />} />
          <Route path="/obs/starting-soon" element={<ObsStartingSoon />} />
          <Route path="/obs/brb" element={<ObsBrb />} />
          <Route path="/obs/ending" element={<ObsEnding />} />
          <Route path="/obs/cam-frame" element={<ObsCamFrame />} />
          <Route path="/obs/cam-frame-round" element={<ObsCamFrameRound />} />
          <Route path="/obs/ozzy-cam" element={<ObsOzzyCam />} />
          <Route path="/obs/stats-ticker" element={<ObsStatsTicker />} />
          <Route path="/obs/stats-panel" element={<ObsStatsPanel />} />
          <Route path="/obs/full-cam-stats" element={<ObsFullcamStats />} />
          <Route path="/obs/full-cam-logo" element={<ObsFullcamStats overlayKey="full-cam-logo" presetDefaults={{ logoOverlay: true, showChat: false, showStrip: false, margin: 70 }} />} />
          <Route path="/obs/dual-cam" element={<ObsFullcamStats overlayKey="dual-cam" presetDefaults={{ dualCam: true }} />} />
          <Route path="/obs/screen-cam-stats" element={<ObsScreenCamStats />} />
          <Route path="/obs/heart-rate" element={<ObsHeartRate />} />
          <Route path="/obs/alerts" element={<ObsAlerts />} />
          <Route path="/obs/mascot-thanks" element={<ObsMascotThanks />} />
          <Route path="/obs/website-promo" element={<ObsWebsitePromo />} />
          <Route path="/obs/chat" element={<ObsChat />} />
          <Route path="/obs/now-playing" element={<ObsNowPlaying />} />
          <Route path="/obs/goal-bar" element={<ObsGoalBar />} />
          <Route path="/obs/social-bar" element={<ObsSocialBar />} />
          <Route path="/obs/border" element={<ObsBorderFrame />} />
          <Route path="/obs/podium" element={<ObsPodium />} />
          <Route path="/obs/stats-border" element={<ObsStatsBorder />} />
          <Route path="/obs/subathon" element={<ObsSubathonTimer />} />
          <Route path="/obs/mascot-talk" element={<ObsMascotTalk />} />
          <Route path="/obs/mascot-cam" element={<ObsMascotCam />} />
          <Route path="/obs/giveaway" element={<ObsGiveaway />} />
          <Route path="/obs/poll" element={<ObsPoll />} />
          <Route path="/obs/poll-full" element={<ObsPollFull />} />
          <Route path="/obs/draw" element={<ObsDraw />} />
          <Route path="/obs/multichat" element={<ObsMultiChat />} />
          <Route path="/obs/multichat/dashboard" element={<ObsMultiChatDashboard />} />
          <Route path="/obs/reward-toast" element={<ObsRewardToast />} />
          <Route path="/obs/scoreboard" element={<ObsScoreboard />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <>
      <BrandBackground />
      <PageviewTracker />
      <InitialSplashGate />
      <Suspense fallback={<SplashScreen />}>
        <Routes>
          <Route path="/onlyfans" element={<OnlyFansPage />} />
          <Route path="/" element={<Home />} />
          <Route path="/vote" element={<VoteRoute />} />
          <Route path="/kijk" element={<WatchRoute />} />
          <Route path="/muziek" element={<MusicRoute />} />
          <Route path="/setup" element={<SetupRoute />} />
          <Route path="/admin" element={<AdminRoute />} />
          <Route path="/admin/mascot-thanks" element={<MascotThanksAdminRoute />} />
          <Route path="/profiel" element={<ProfileRoute />} />
          <Route path="/suggesties" element={<SuggestionsHubRoute />} />
          <Route path="/hoe-suggereren" element={<HowToSuggestRoute />} />
          <Route path="/socials" element={<SocialsRoute />} />
          <Route path="/doneren" element={<TipRoute />} />
          <Route path="/doneren/bedankt" element={<DonateThanksPage />} />
          <Route path="/tip" element={<TipRoute />} />
          <Route path="/bits" element={<BitsRoute />} />
          <Route path="/support" element={<SupportRoute />} />
          <Route path="/forum" element={<ForumRoute />} />
          <Route path="/forum/:id" element={<TopicRoute />} />
          <Route path="/grijpmachine" element={<GrijpmachinePage />} />
          <Route path="/slotmachine" element={<SlotmachinePage />} />
          <Route path="/rad" element={<RadPage />} />
          <Route path="/rad-giveaway" element={<GiveawayPage />} />
          <Route path="/poll" element={<PollPage />} />
          <Route path="/polls" element={<PollsHubRoute />} />
          <Route path="/tekenen" element={<DrawRoute />} />
          <Route path="/kanaalpunten" element={<ChannelPointsRoute />} />
        </Routes>
      </Suspense>
      {location.pathname !== '/onlyfans' && (
        <>
          <LiveNowFloat />
          <FollowerTicker />
        </>
      )}
      <LoginDialog />
    </>
  );
}

export default App;
